// Рисунки (4.5): регистрация команд, ленты, контекстной вкладки «Рисунок», вставки (Ctrl+V, перетаскивание,
// несколько файлов, из интернета, из проекта), отображения узла и живых правил.
import type { Ctx, RegistryApi, LintIssue } from '../../../core/registry';
import { NodeSelection, TextSelection } from '@tiptap/pm/state';
import { naturalCm, pickFiles, TEXT_W_CM, cmToWidth, widthToCm } from '../util';
import { FigureView, figureStopEvent } from './FigureView';
import { CropDialog, FigureDialog, GalleryDialog, UrlDialog } from './dialogs';
import { assetInfo, insertFromUrl, insertImageFiles, isImage, replaceImage, replacePart, transformImage } from './upload';
import { figureAt } from './common';

const IMG_ACCEPT = 'image/*,.png,.jpg,.jpeg,.gif,.bmp,.tif,.tiff,.webp,.svg,.emf,.wmf';
const hasFigure = (c: Ctx) => !!figureAt(c);
/** Обычный рисунок (одно изображение): размер, поворот, обрезка - у составного части раскладывает сборка. */
const isComposite = (c: Ctx) => { const f = figureAt(c); return !!f && Array.isArray(f.node.attrs.images) && f.node.attrs.images.length > 1; };
const hasSingle = (c: Ctx) => hasFigure(c) && !isComposite(c);

/** Есть ли в HTML буфера текст (Word кладёт и текст, и картинку-снимок фрагмента). */
function htmlHasText(html: string): boolean {
  if (!html) return false;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return (doc.body.textContent || '').replace(/[\s ]+/g, '').length > 0;
}

export function setupFigures(r: RegistryApi) {
  // ---------------------------------------------------------------------------------------- команды
  r.commands.add({ id: 'insert.figure', title: 'Рисунок', icon: 'image', shortcut: 'Mod-Alt-i',
    description: 'Вставить изображения из файлов (можно несколько; а ещё - Ctrl+V скриншота и перетаскивание)',
    run: async (c) => { const files = await pickFiles(IMG_ACCEPT); if (files.length) await insertImageFiles(c, files, { captions: true }); } });
  r.commands.add({ id: 'figure.fromUrl', title: 'Рисунок из интернета…', icon: 'tfc-url', run: (c) => c.openDialog('tfc.figureUrl') });
  r.commands.add({ id: 'figure.gallery', title: 'Изображения проекта…', icon: 'tfc-gallery', description: 'Вставить уже загруженное изображение',
    run: (c) => c.openDialog('tfc.gallery') });
  r.commands.add({ id: 'figure.props', title: 'Свойства рисунка…', icon: 'settings', isEnabled: hasFigure,
    description: 'Ширина в сантиметрах, замещающий текст, заимствование', run: (c, arg) => c.openDialog('tfc.figure', { focus: arg }) });
  r.commands.add({ id: 'figure.width', title: 'Ширина рисунка', icon: 'tfc-fit', isEnabled: hasSingle, run: (c, arg) => {
    const f = figureAt(c);
    if (!f) return;
    const info = f.node.attrs.src ? assetInfo(f.node.attrs.src) : null;
    let width: string | null = null;
    if (arg === 'full') width = `${TEXT_W_CM}cm`;
    else if (arg === 'half') width = `${TEXT_W_CM / 2}cm`;
    else if (arg === 'natural') width = null;
    else if (typeof arg === 'number') width = cmToWidth(arg);
    else if (typeof arg === 'string') width = widthToCm(arg) ? arg : null;
    if (info && arg === 'natural' && naturalCm(info.width, info.dpi) > TEXT_W_CM) c.toast('Исходный размер шире поля - в отчёте рисунок будет 17 см', 'info', 3000);
    c.editor!.view.dispatch(c.editor!.state.tr.setNodeMarkup(f.pos, undefined, { ...f.node.attrs, width }));
  } });
  r.commands.add({ id: 'figure.rotate', title: 'Повернуть рисунок', icon: 'tfc-rotate-cw', isEnabled: hasSingle, run: async (c, arg) => {
    const f = figureAt(c);
    if (f) await transformImage(c, f.node.attrs.id, { rotate: ((Number(arg) || 90) + 360) % 360 });
  } });
  r.commands.add({ id: 'figure.rotateLeft', title: 'Повернуть влево', icon: 'tfc-rotate-ccw', isEnabled: hasSingle, run: (c) => c.run('figure.rotate', -90) });
  r.commands.add({ id: 'figure.rotateRight', title: 'Повернуть вправо', icon: 'tfc-rotate-cw', isEnabled: hasSingle, run: (c) => c.run('figure.rotate', 90) });
  r.commands.add({ id: 'figure.flip', title: 'Отразить по горизонтали', icon: 'tfc-flip', isEnabled: hasSingle, run: async (c) => {
    const f = figureAt(c);
    if (f) await transformImage(c, f.node.attrs.id, { flip: 'h' });
  } });
  r.commands.add({ id: 'figure.crop', title: 'Обрезать…', icon: 'tfc-crop', isEnabled: hasSingle, run: (c) => c.openDialog('tfc.crop') });
  r.commands.add({ id: 'figure.replace', title: 'Заменить изображение…', icon: 'tfc-replace', isEnabled: hasFigure,
    description: 'Другой файл - подпись, ширина и ссылки сохраняются', run: async (c) => {
      const f = figureAt(c);
      if (!f) return;
      const files = await pickFiles(IMG_ACCEPT, false);
      if (files[0] && isComposite(c)) await replacePart(c, f.node.attrs.id, 0, files[0]);     // составной - первая часть
      else if (files[0]) await replaceImage(c, f.node.attrs.id, files[0]);
    } });
  r.commands.add({ id: 'figure.replacePart', title: 'Изображение части рисунка…', icon: 'tfc-replace', isEnabled: hasFigure,
    description: 'Файл для части «а», «б» … составного рисунка', run: async (c, arg) => {
      const f = figureAt(c);
      const k = Number(arg);
      if (!f || !Number.isInteger(k)) return;
      const files = await pickFiles(IMG_ACCEPT, false);
      if (files[0]) await replacePart(c, f.node.attrs.id, k, files[0]);
    } });
  r.commands.add({ id: 'figure.delete', title: 'Удалить рисунок', icon: 'trash', isEnabled: hasFigure, run: (c) => {
    const f = figureAt(c);
    if (f) c.editor!.chain().focus().deleteRange({ from: f.pos, to: f.pos + f.node.nodeSize }).run();
  } });
  r.commands.add({ id: 'figure.select', title: 'Выделить рисунок', icon: 'image', isEnabled: hasFigure, run: (c) => {
    const f = figureAt(c);
    if (f) c.editor!.view.dispatch(c.editor!.state.tr.setSelection(NodeSelection.create(c.editor!.state.doc, f.pos)));
  } });

  // ---------------------------------------------------------------------------------------- лента
  r.ribbonGroups.add({ id: 'insert.objects', tab: 'insert', title: 'Иллюстрации и таблицы', order: 10 });
  r.ribbonItems.add({ id: 'figure', group: 'insert.objects', type: 'split', command: 'insert.figure', icon: 'image', order: 10,
    items: [
      { command: 'insert.figure', label: 'Из файлов… (можно несколько)', icon: 'image' },
      { command: 'figure.fromUrl' },
      { command: 'figure.gallery' },
      { separator: true },
      { label: 'Подсказка: скриншот (Win+Shift+S) - просто Ctrl+V; файлы можно перетащить в текст', icon: 'info', disabled: true },
    ] });

  r.ribbonTabs.add({ id: 'figure', title: 'Рисунок', order: 56, contextual: hasFigure });
  r.ribbonGroups.add({ id: 'figure.size', tab: 'figure', title: 'Размер', order: 10 });
  r.ribbonGroups.add({ id: 'figure.image', tab: 'figure', title: 'Изображение', order: 20 });
  r.ribbonGroups.add({ id: 'figure.more', tab: 'figure', title: 'Рисунок', order: 30 });
  r.ribbonItems.add({ id: 'f-full', group: 'figure.size', type: 'button', command: 'figure.width', arg: 'full', icon: 'tfc-fit', label: 'По ширине поля (17 см)', order: 0 });
  r.ribbonItems.add({ id: 'f-half', group: 'figure.size', type: 'button', command: 'figure.width', arg: 'half', icon: 'tfc-width', label: 'Половина (8,5 см)', order: 1 });
  r.ribbonItems.add({ id: 'f-natural', group: 'figure.size', type: 'button', command: 'figure.width', arg: 'natural', icon: 'tfc-ruler', label: 'Исходный размер', order: 2 });
  ['rotateLeft', 'rotateRight', 'flip', 'crop', 'replace'].forEach((id, i) =>
    r.ribbonItems.add({ id: 'f-' + id, group: 'figure.image', type: 'button', command: 'figure.' + id, label: r.commands.get('figure.' + id)!.title, order: i }));
  r.ribbonItems.add({ id: 'f-props', group: 'figure.more', type: 'large', command: 'figure.props', order: 0 });
  r.ribbonItems.add({ id: 'f-delete', group: 'figure.more', type: 'large', command: 'figure.delete', order: 1 });

  r.contextMenu.add({ id: 'tfc.figure', order: 11, items: (_c, target) => {
    if (!target.nodes.some((n) => n.type === 'figure')) return null;
    return [
      { label: 'Размер', icon: 'tfc-fit', children: [
        { command: 'figure.width', arg: 'full', label: 'По ширине поля (17 см)' }, { command: 'figure.width', arg: 'half', label: 'Половина (8,5 см)' },
        { command: 'figure.width', arg: 'natural', label: 'Исходный' }, { command: 'figure.width', arg: 12, label: '12 см' }, { command: 'figure.width', arg: 10, label: '10 см' }] },
      { command: 'figure.rotateLeft' }, { command: 'figure.rotateRight' }, { command: 'figure.crop' }, { command: 'figure.replace' },
      { separator: true }, { command: 'figure.props' }, { command: 'figure.delete' },
    ];
  } });

  r.dialogs.add({ id: 'tfc.figure', component: FigureDialog });
  r.dialogs.add({ id: 'tfc.crop', component: CropDialog });
  r.dialogs.add({ id: 'tfc.figureUrl', component: UrlDialog });
  r.dialogs.add({ id: 'tfc.gallery', component: GalleryDialog });
  r.nodeViews.add({ id: 'figure', component: FigureView, options: { as: 'figure', stopEvent: figureStopEvent } });

  // ---------------------------------------------------------------------------------------- вставка
  // файлы-картинки: скриншот (Ctrl+V), файлы из Проводника (вставка и перетаскивание, несколько сразу);
  // картинка на выделенный рисунок или брошенная на рисунок - замена изображения (подпись сохраняется)
  r.paste.add({ id: 'tfc.imageFiles', priority: 101,
    match: (d) => d.files.length > 0 && d.files.some(isImage) && !htmlHasText(d.html),
    handle: (d, c) => {
      const files = d.files.filter(isImage);
      const ed = c.editor!;
      let target: string | null = null;
      if (files.length === 1) {
        if (d.kind === 'drop') {
          const el = (d.event.target as HTMLElement | null)?.closest?.('.tfc-figure .tfc-fig-box');
          if (el) { try { const p = ed.view.posAtDOM(el, 0); const $p = ed.state.doc.resolve(p); for (let k = $p.depth; k >= 0; k--) if ($p.node(k).type.name === 'figure') target = $p.node(k).attrs.id; } catch { /* нет узла */ } }
        } else {
          const sel = ed.state.selection as any;
          if (sel.node?.type.name === 'figure') target = sel.node.attrs.id;
        }
      }
      if (target) return replaceImage(c, target, files[0]);
      return insertImageFiles(c, files, { at: d.kind === 'drop' ? dropBlockPos(c, d.pos) : null, captions: d.kind === 'drop' || files.some((f) => !!f.name && !/^image\.\w+$/i.test(f.name)) });
    } });

  // HTML с одной картинкой по адресу http(s) (копия картинки со страницы без файла) - скачивает сервер
  r.paste.add({ id: 'tfc.htmlRemoteImage', priority: 79,
    match: (d) => d.kind === 'paste' && !d.files.length && /<img[^>]+src="?https?:/i.test(d.html) && !htmlHasText(d.html) && (d.html.match(/<img/gi) || []).length === 1,
    handle: async (d, c) => {
      const src = new DOMParser().parseFromString(d.html, 'text/html').querySelector('img')?.getAttribute('src');
      if (!src) return false;
      c.toast('Загрузка изображения…', 'info', 2000);
      await insertFromUrl(c, src);
      c.toast('Рисунок вставлен - укажите источник в свойствах рисунка (4.5.2)', 'ok', 4000);
    } });

  // ---------------------------------------------------------------------------------------- живые правила
  r.lint.add({ id: 'tfc.figures', title: 'Рисунки', run: (ix) => {
    const out: LintIssue[] = [];
    const bySrc = new Map<string, string[]>();
    for (const e of ix.entries) {
      if (e.type !== 'figure') continue;
      const src = e.attrs.src as string;
      if (!src) {
        // место под рисунок (импорт .docx: диаграмма/фигуры Word не перенесены, подпись без картинки): в отчёте -
        // рамка «Рисунок не перенесён», проверка по СТО - E FIG-IMAGE-MISSING
        out.push({ sev: e.attrs.missing ? 'E' : 'W', node: e.id, rule: '4.5',
          msg: `${e.num?.label || 'Рисунок'}: нет изображения - в отчёте на его месте будет рамка «Рисунок не перенесён»; вставьте изображение` });
        continue;
      }
      bySrc.set(src, [...(bySrc.get(src) || []), e.id]);
      const info = assetInfo(src);
      if (info) {
        const w = widthToCm(e.attrs.width) ?? Math.min(TEXT_W_CM, naturalCm(info.width, info.dpi));
        const dpi = info.width / (Math.min(w, TEXT_W_CM) / 2.54);
        if (/\.jpe?g$/i.test(src) && dpi < 150) out.push({ sev: 'W', node: e.id, rule: '4.5.2',
          msg: `${e.num?.label || 'Рисунок'}: ≈${Math.round(dpi)} dpi - для сканов/фото печатных материалов нужно не менее 150 dpi`,
          fix: { label: 'Уменьшить до 150 dpi', run: (c) => { gotoFig(c, e.id); c.run('figure.width', Math.floor((info.width / 150) * 2.54 * 10) / 10); } } });
        // слишком тяжёлая картинка (> 5 МБ или > 400 dpi при своей ширине): DOCX раздувается (сдача в ЭИОС)
        if ((info.size || 0) > 5 * 1048576 || dpi > 400) {
          const k = Math.min(0.99, 300 / dpi);
          out.push({ sev: 'I', node: e.id, rule: '4.5.2',
            msg: `${e.num?.label || 'Рисунок'}: картинка ${((info.size || 0) / 1048576).toFixed(1)} МБ, ≈${Math.round(dpi)} dpi - для печати достаточно 300 dpi, отчёт станет легче`,
            fix: dpi > 310 ? { label: 'Уменьшить до 300 dpi', run: (c) => { void transformImage(c, e.id, { scale: k }).then(() => c.toast('Картинка уменьшена до 300 dpi (Ctrl+Z - вернуть)', 'ok')); } } : undefined });
        }
      }
      const cap = e.node.firstChild?.textContent.trim() || '';
      if (/^рис(унок|\.)?\s*\d/i.test(cap)) out.push({ sev: 'W', node: e.id, rule: '4.5.6',
        msg: `${e.num?.label || 'Рисунок'}: «Рисунок N.» ставится автоматически - в подписи оставьте только название` });
      if (cap && /[^.]\.$/.test(cap) && !/\b(т\.\s?[дп]|др|пр|см|рис|г|гг)\.$/i.test(cap)) out.push({ sev: 'W', node: e.id, rule: '4.5.7',
        msg: `${e.num?.label || 'Рисунок'}: точку в конце подписи не ставят` });
      if (cap && /^[а-яё]/.test(cap)) out.push({ sev: 'W', node: e.id, rule: '4.5.7', msg: `${e.num?.label || 'Рисунок'}: название пишут с прописной буквы` });
    }
    for (const ids of bySrc.values()) if (ids.length > 1) for (const id of ids.slice(1))
      out.push({ sev: 'W', node: id, rule: '4.5.2', msg: 'Это изображение уже есть в другом рисунке - иллюстрации не должны дублировать друг друга (сошлитесь на первый)' });
    return out;
  } });
}

function gotoFig(c: Ctx, id: string) {
  const ed = c.editor;
  if (!ed) return;
  ed.state.doc.descendants((n, pos) => {
    if (n.attrs?.id === id) { ed.view.dispatch(ed.state.tr.setSelection(NodeSelection.create(ed.state.doc, pos))); return false; }
    return true;
  });
}

/** Позиция сброса -> граница блока верхнего уровня (рисунок не может стоять внутри абзаца). */
function dropBlockPos(c: Ctx, pos: number | null): number | null {
  const ed = c.editor;
  if (!ed || pos === null) return null;
  const $p = ed.state.doc.resolve(Math.min(pos, ed.state.doc.content.size));
  let d = $p.depth;
  while (d > 0 && !['doc', 'appendix'].includes($p.node(d - 1).type.name)) d--;
  if (d === 0) return pos;
  // в верхнюю половину блока - перед ним, иначе - после
  const blk = $p.node(d);
  const start = $p.before(d);
  // пустой абзац - заменить им (как вставка Ctrl+V), а не оставлять лишней пустой строкой перед рисунком
  if (blk.type.name === 'paragraph' && blk.content.size === 0) {
    ed.view.dispatch(ed.state.tr.setSelection(TextSelection.create(ed.state.doc, start + 1)));
    return null;
  }
  return pos - start < blk.nodeSize / 2 && blk.type.name !== 'paragraph' ? start : $p.after(d);
}
